import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { paymentService } from '../../services/payment.service';
import { Payment } from '../../types';
import { Loading } from '../../components/ui/Loading';
import { Button } from '../../components/ui/Button';

declare global {
  interface Window {
    Razorpay: any;
  }
}

export const PaymentsPage = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const data = await paymentService.getMemberPayments();
      setPayments(data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (amount: number, type: string) => {
    try {
      setProcessing(true);
      // Initiate payment
      const order = await paymentService.initiatePayment(amount, type);

      const options = {
        key: import.meta.env.VITE_RAZORPAY_KEY_ID || 'rzp_test_PlaceHolder', // Replace with valid test key if env missing
        amount: order.amount,
        currency: order.currency,
        name: 'Hospital Membership',
        description: `Payment for ${type}`,
        order_id: order.razorpayOrderId,
        handler: async (response: any) => {
          try {
            await paymentService.verifyPayment({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              amount: amount,
              type: type
            });
            toast.success('Payment successful!');
            loadPayments();
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: 'Member',
          email: 'member@example.com',
          contact: '9999999999'
        },
        theme: {
          color: '#3B82F6'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error: any) {
      toast.error(error.message || 'Failed to initiate payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadInvoice = async (paymentId: string) => {
    try {
      const invoice = await paymentService.getInvoiceById(paymentId); // Assuming this returns invoice details

      // Create a simple text content for the invoice
      const invoiceContent = `
       INVOICE
       --------------------------------
       Invoice Number: ${invoice.invoiceNumber}
       Date: ${new Date(invoice.generatedAt).toLocaleDateString()}
       
       Bill To:
       Member ID: ${invoice.payment?.member?.membershipId || 'N/A'}
       Name: ${invoice.payment?.member?.name || 'Valued Member'}
       
       --------------------------------
       Description           Amount
       --------------------------------
       ${invoice.payment?.type}       ₹${invoice.amount}
       Tax (18%)             ₹${invoice.taxAmount}
       --------------------------------
       TOTAL                 ₹${invoice.totalAmount}
       --------------------------------
       Status: PAID
       `;

      const blob = new Blob([invoiceContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${invoice.invoiceNumber}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount: number) => {
    return `₹${(amount).toFixed(2)}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payments & Invoices</h1>
          <p className="text-gray-600">View payment history and download invoices</p>
        </div>
        <Button
          onClick={() => handlePayment(1000, 'MEMBERSHIP_RENEWAL')} // Example amount and type
          disabled={processing}
        >
          {processing ? 'Processing...' : 'Pay Membership Renewal (₹1000)'}
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {payments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No payment history found
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {formatDate(payment.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {payment.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {formatAmount(payment.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(payment.status)}`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {payment.status === 'SUCCESS' && (
                      <button
                        onClick={() => payment.invoice && handleDownloadInvoice(payment.invoice.id)} // Assuming invoice relationship exists in fetched data or fetch via payment ID usually
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Download Invoice
                      </button>
                    )}
                    <div className="text-xs text-gray-400 mt-1">{payment.razorpayPaymentId || '-'}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-6 bg-blue-50 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-2">💡 Payment Information</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• All payments are processed securely through Razorpay</li>
          <li>• Invoices are automatically generated for successful payments</li>
          <li>• Contact support if you have any payment-related queries</li>
        </ul>
      </div>
    </div>
  );
};
